import React from "react";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  ShieldCheck, 
  Users, 
  Award,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  CheckCircle
} from "lucide-react";
import { Link } from "react-router-dom";
import logo from "../assets/images/logo.png";

const Footer = () => {
  const services = [
    { name: "Residential Cleaning", slug: "residential-cleaning" },
    { name: "Commercial Cleaning", slug: "commercial-cleaning" },
    { name: "Carpet & Upholstery", slug: "carpet-upholstery-cleaning" },
    { name: "Duct & Vent Cleaning", slug: "duct-vent-cleaning" },
    { name: "End of Lease Cleaning", slug: "end-of-lease-cleaning" },
    { name: "Window & Gutter Cleaning", slug: "window-gutter-cleaning" },
    { name: "Emergency Restoration", slug: "emergency-restoration" },
    { name: "Deep Cleaning", slug: "deep-cleaning" },
    { name: "Rug Cleaning", slug: "rug-cleaning" },
    { name: "Mattress Cleaning", slug: "mattress-cleaning" },
    { name: "Rugs Deep Cleaning", slug: "rugs-deep-cleaning" },
    { name: "Curtain Cleaning", slug: "curtain-cleaning" },
  ];

  const companyLinks = [
    { name: "About Us", path: "/about" },
    { name: "Our Team", path: "/team" },
    { name: "Careers", path: "/careers" },
    { name: "Testimonials", path: "/testimonials" },
    { name: "Blog", path: "/blog" },
    { name: "Gallery", path: "/gallery" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  const policies = [
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms of Service", path: "/terms" },
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h2 className="text-2xl font-bold mb-2">Subscribe to Our Newsletter</h2>
              <p className="text-blue-100">Get cleaning tips, exclusive offers, and updates</p>
            </div>
            <div className="w-full lg:w-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="px-6 py-3 rounded-full text-gray-900 w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <button className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap">
                  Subscribe
                </button>
              </div>
              <p className="text-xs text-blue-200 mt-2 text-center sm:text-left">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <img
                src={logo}
                alt="Knight Cleanings"
                className="h-16 w-16 rounded-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div>
                <h2 className="text-2xl font-bold group-hover:text-amber-400 transition-colors">
                  Knight Cleanings
                </h2>
                <p className="text-blue-300 text-sm font-medium">PREMIUM CLEANING SOLUTIONS</p>
              </div>
            </Link>
            <p className="text-gray-300 mb-6">
              Professional cleaning services with over 15 years of experience. 
              We deliver exceptional results for residential and commercial clients across Melbourne.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/knightcleanings"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-blue-600 flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.instagram.com/knightcleaningsolutions?igsh=b21uaDRicXRvc2p2&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-pink-600 flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://www.linkedin.com/company/knight-cleanings/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-blue-700 flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://www.youtube.com/@KnightCleanings"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-800 hover:bg-red-600 flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="YouTube"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">
              Our Services
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-gray-300 hover:text-amber-400 transition-colors flex items-center gap-2 group"
                  >
                    <CheckCircle size={14} className="text-blue-500 group-hover:text-amber-400 transition-colors" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {service.name}
                    </span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/services"
                  className="text-blue-400 hover:text-amber-400 font-semibold transition-colors flex items-center gap-2 mt-4"
                >
                  <span>View All Services</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {companyLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-amber-400 transition-colors group flex items-center gap-2"
                  >
                    <svg 
                      className="w-3 h-3 text-gray-500 group-hover:text-amber-400 transition-colors" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    {link.name}
                  </Link>
                </li>
              ))}
              <li className="mt-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                >
                  <Clock size={16} />
                  Book Now
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">
              Contact Us
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 group">
                <div className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <Phone className="text-amber-400" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Call Us Anytime</p>
                  <a 
                    href="tel:+61420499661" 
                    className="text-gray-300 hover:text-amber-400 transition-colors font-semibold"
                  >
                    +61 420 499 661
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-3 group">
                <div className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <Mail className="text-amber-400" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Email Us</p>
                  <a 
                    href="mailto:info@knightcleanings.com" 
                    className="text-gray-300 hover:text-amber-400 transition-colors"
                  >
                    info@knightcleanings.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-3 group">
                <div className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <Clock className="text-amber-400" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Business Hours</p>
                  <p className="text-gray-300">
                    Mon-Fri: 8AM-6PM<br />
                    Sat-Sun: 9AM-6PM
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 group">
                <div className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <MapPin className="text-amber-400" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Service Areas</p>
                  <p className="text-gray-300">
                    Melbourne & Surrounding Suburbs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="flex items-center gap-3 group">
                <div className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-green-900 transition-colors">
                  <ShieldCheck size={24} className="text-green-400" />
                </div>
                <div>
                  <p className="font-semibold group-hover:text-green-400 transition-colors">Licensed & Insured</p>
                  <p className="text-sm text-gray-400">Full Coverage</p>
                </div>
              </div>
              <div className="flex items-center gap-3 group">
                <div className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-blue-900 transition-colors">
                  <Users size={24} className="text-blue-400" />
                </div>
                <div>
                  <p className="font-semibold group-hover:text-blue-400 transition-colors">Expert Team</p>
                  <p className="text-sm text-gray-400">15+ Years Experience</p>
                </div>
              </div>
              <div className="flex items-center gap-3 group">
                <div className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-amber-900 transition-colors">
                  <Award size={24} className="text-amber-400" />
                </div>
                <div>
                  <p className="font-semibold group-hover:text-amber-400 transition-colors">5-Star Rated</p>
                  <p className="text-sm text-gray-400">500+ Reviews</p>
                </div>
              </div>
              <div className="flex items-center gap-3 group">
                <div className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-purple-900 transition-colors">
                  <Clock size={24} className="text-purple-400" />
                </div>
                <div>
                  <p className="font-semibold group-hover:text-purple-400 transition-colors">24/7 Service</p>
                  <p className="text-sm text-gray-400">Emergency Available</p>
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <img 
                src="https://images.unsplash.com/photo-1611224923853-80b023f02d71?q=80&w=2069&auto=format&fit=crop" 
                alt="Green Certified" 
                className="h-12 rounded-lg object-cover opacity-80 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-gray-950 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Knight Cleanings. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                ABN: 34 692 576 319 | ACN: 889969421
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
              {policies.map((policy, index) => (
                <Link
                  key={index}
                  to={policy.path}
                  className="hover:text-amber-400 transition-colors"
                >
                  {policy.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;